import {
  AixContentBody,
  AixContentHeader,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  useScreenSize,
} from "@aix/ui"
import { createFileRoute } from "@tanstack/react-router"
import { z } from "zod"

import CustomHead from "~/components/SEOHead/CustomHead"
import { HeaderBreadcrumb } from "~/components/Tools/MediaGeneration/HeaderBreadcrumb"
import { VideoEditorCanvas } from "~/components/VideoEditor/VideoEditorCanvas"
import { VideoEditorProvider } from "~/components/VideoEditor/VideoEditorContext"
import { VideoEditorMobileBody } from "~/components/VideoEditor/VideoEditorMobileBody"
import { VideoEditorShortcuts } from "~/components/VideoEditor/VideoEditorShortcuts"
import { VideoEditorSidebar } from "~/components/VideoEditor/VideoEditorSidebar"
import { VideoEditorTimeline } from "~/components/VideoEditor/VideoEditorTimeline"
import { VideoEditorTopBar } from "~/components/VideoEditor/VideoEditorTopBar"
import { createEditorEffectDemoState } from "~/components/VideoEditor/utils"
import type { EditorEffectDemoScope } from "~/components/VideoEditor/utils"
import AppLayout from "~/layouts/AppLayout"

const editSearchSchema = z.object({
  videoId: z.string().optional(),
  effectPreset: z.string().optional(),
  effectScope: z.enum(["track", "project"]).optional(),
})

function VideoEditorDesktopBody() {
  return (
    <AixContentBody className="w-full p-0 overflow-hidden flex-1" innerClassName="w-full h-full max-w-none">
      <ResizablePanelGroup direction="vertical" className="flex-1 min-h-0 overflow-hidden">
        <ResizablePanel defaultSize={65} minSize={40}>
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel minSize={40}>
              <VideoEditorCanvas />
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={30} minSize={22} className="min-w-[320px]">
              <VideoEditorSidebar />
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel defaultSize={35} minSize={20}>
          <VideoEditorTimeline />
        </ResizablePanel>
      </ResizablePanelGroup>
    </AixContentBody>
  )
}

function VideoEditorPage() {
  const { breakpoint } = useScreenSize()
  const isCompact = breakpoint.lg

  return (
    <>
      {/* The breadcrumb only repeats the page name; on phones the vertical space goes to the preview. */}
      <AixContentHeader className="hidden flex-col w-full border-b lg:flex" innerClassName="min-w-full">
        <HeaderBreadcrumb title="Video Editor" />
      </AixContentHeader>

      <VideoEditorTopBar />
      <VideoEditorShortcuts />

      {isCompact ? <VideoEditorMobileBody /> : <VideoEditorDesktopBody />}
    </>
  )
}

function VideoEditorPageWrapper() {
  const { videoId, effectPreset, effectScope } = Route.useSearch()
  const demoScope: EditorEffectDemoScope = effectScope ?? "track"
  const initialDemoState = videoId ? null : createEditorEffectDemoState(effectPreset, demoScope)
  const initialSeedKey = initialDemoState ? `${effectPreset ?? ""}:${demoScope}` : null
  return (
    <AppLayout navCollapsible="icon" navIconFloat fixedViewport>
      <CustomHead title="Video Editor" description="Assemble clips and audio into a rendered video." />
      <div className="flex min-h-0 flex-1 flex-col w-full">
        <VideoEditorProvider
          key={`${videoId ?? "new"}:${initialSeedKey ?? "blank"}`}
          videoId={videoId ?? null}
          initialState={initialDemoState}
          initialSeedKey={initialSeedKey}
        >
          <VideoEditorPage />
        </VideoEditorProvider>
      </div>
    </AppLayout>
  )
}

export const Route = createFileRoute("/create/edit")({
  component: VideoEditorPageWrapper,
  validateSearch: editSearchSchema,
})
